import { Suspense, lazy, useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import Home from "@/pages/Home";
import { persistUTMs } from "@/utils/utm";

// Lazy-load non-critical shell components
const Footer = lazy(() => import("@/components/Footer"));
const CustomCursor = lazy(() => import("@/components/CustomCursor"));
const QuickActionHub = lazy(() => import("@/components/QuickActionHub"));
const GrainToggle = lazy(() => import("@/components/GrainToggle"));
const CookieConsent = lazy(() => import("@/components/CookieConsent"));
const ToasterWrapper = lazy(() =>
  import("@/components/ui/sonner").then((m) => ({ default: m.Toaster }))
);

// Lazy-load secondary pages
const Services = lazy(() => import("@/pages/Services"));
const Work = lazy(() => import("@/pages/Work"));
const About = lazy(() => import("@/pages/About"));
const Contact = lazy(() => import("@/pages/Contact"));
const Terms = lazy(() => import("@/pages/Terms"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const Journal = lazy(() => import("@/pages/Journal"));
const JournalDetail = lazy(() => import("@/pages/JournalDetail"));
const Leads = lazy(() => import("@/pages/Leads"));
const NotFound = lazy(() => import("@/pages/NotFound"));

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);
  return null;
}

// Clean non-blocking page wrapper
const PageTransition = ({ children }) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25, ease: "easeOut" }}
    >
      {children}
    </motion.div>
  );
};

function AnimatedRoutes() {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<Home />} />
        <Route path="/services" element={<PageTransition><Services /></PageTransition>} />
        <Route path="/work" element={<PageTransition><Work /></PageTransition>} />
        <Route path="/about" element={<PageTransition><About /></PageTransition>} />
        <Route path="/contact" element={<PageTransition><Contact /></PageTransition>} />
        <Route path="/journal" element={<PageTransition><Journal /></PageTransition>} />
        <Route path="/journal/:id" element={<PageTransition><JournalDetail /></PageTransition>} />
        <Route path="/leads" element={<PageTransition><Leads /></PageTransition>} />
        <Route path="/terms" element={<PageTransition><Terms /></PageTransition>} />
        <Route path="/privacy" element={<PageTransition><Privacy /></PageTransition>} />
        <Route path="*" element={<PageTransition><NotFound /></PageTransition>} />
      </Routes>
    </AnimatePresence>
  );
}

function App() {
  const [mountNonCritical, setMountNonCritical] = useState(false);

  useEffect(() => {
    // Persist inbound UTM campaign parameters
    persistUTMs();

    // Schedule non-critical shell overlays after initial paint to keep main-thread TBT low
    if (typeof window !== "undefined" && "requestIdleCallback" in window) {
      const handle = window.requestIdleCallback(() => setMountNonCritical(true));
      return () => window.cancelIdleCallback(handle);
    } else {
      const timer = setTimeout(() => setMountNonCritical(true), 150);
      return () => clearTimeout(timer);
    }
  }, []);

  return (
    <div className="App" data-testid="inkaa-app-root">
      {/* Global Grain Overlay */}
      <div className="ink-grain-overlay" />
      
      <BrowserRouter>
        <ScrollToTop />
        {mountNonCritical && (
          <Suspense fallback={null}>
            <CustomCursor />
            <GrainToggle />
            <QuickActionHub />
            <CookieConsent />
          </Suspense>
        )}
        <Navbar />
        <main>
          <Suspense fallback={<div className="min-h-screen bg-[#050505]" />}>
            <AnimatedRoutes />
          </Suspense>
        </main>
        {mountNonCritical && (
          <Suspense fallback={null}>
            <Footer />
            <ToasterWrapper
              theme="dark"
              position="bottom-center"
              toastOptions={{
                style: {
                  background: "#0F0F0F",
                  border: "1px solid rgba(255,255,255,0.12)",
                  color: "#F5F5F5",
                },
              }}
            />
          </Suspense>
        )}
      </BrowserRouter>
    </div>
  );
}

export default App;
